import { useState, useEffect, useCallback } from 'react';
import { authService } from '../services/auth.service';
import { AuthState, LoginCredentials, AuthUser } from '../types';

function hasAdminPrivileges(groups: string[]): boolean {
  return groups.includes('admin') || groups.includes('root');
}

interface UseAuthReturn extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
}

export function useAuth(): UseAuthReturn {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    token: null,
  });

  useEffect(() => {
    const checkAuth = async (): Promise<void> => {
      try {
        const user = await authService.getCurrentUser();
        const token = await authService.getToken();

        // Check if user has admin privileges (admin or root)
        if (user && !hasAdminPrivileges(user.groups)) {
          await authService.logout();
          setState({
            isAuthenticated: false,
            isLoading: false,
            user: null,
            token: null,
          });
          return;
        }

        setState({
          isAuthenticated: !!user,
          isLoading: false,
          user,
          token,
        });
      } catch {
        setState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          token: null,
        });
      }
    };

    checkAuth();
  }, []);

  useEffect(() => {
    const unsubscribe = authService.onSessionExpired(() => {
      setState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        token: null,
      });
    });

    return unsubscribe;
  }, []);

  const login = useCallback(async (credentials: LoginCredentials): Promise<void> => {
    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      const { user, token } = await authService.login(credentials);

      // Check if user has admin privileges (admin or root)
      if (!hasAdminPrivileges(user.groups)) {
        await authService.logout();
        throw new Error('Access denied. Admin privileges required.');
      }

      setState({
        isAuthenticated: true,
        isLoading: false,
        user,
        token,
      });
    } catch (error) {
      setState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        token: null,
      });
      throw error;
    }
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    await authService.logout();
    setState({
      isAuthenticated: false,
      isLoading: false,
      user: null,
      token: null,
    });
  }, []);

  return {
    ...state,
    login,
    logout,
  };
}

interface AuthContextValue extends UseAuthReturn {
  setUser: (user: AuthUser | null) => void;
}

import { createContext, useContext, ReactNode } from 'react';

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const auth = useAuth();

  const setUser = useCallback((_user: AuthUser | null): void => {
    // This is handled internally by login/logout
  }, []);

  return <AuthContext.Provider value={{ ...auth, setUser }}>{children}</AuthContext.Provider>;
}

export function useAuthContext(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}
